import type {
  CmmdDeviceRegistryDevice,
  CmmdDeviceRegistryHost,
  CmmdDeviceRegistryRuntime,
  EnvironmentId,
} from "@cmmd-forge/contracts";

export interface RegisteredDesktopRuntimeGroup {
  readonly runtime: CmmdDeviceRegistryRuntime;
  readonly device: CmmdDeviceRegistryDevice;
  readonly label: string;
  readonly platformOs: string | null;
}

export interface RegisteredDesktopRuntimeMapInput {
  readonly registeredRuntimes: readonly CmmdDeviceRegistryRuntime[] | undefined;
  readonly registeredDevices: readonly CmmdDeviceRegistryDevice[] | undefined;
  readonly registeredHosts: readonly CmmdDeviceRegistryHost[] | undefined;
  readonly currentRegisteredRuntimeId: string | null | undefined;
  readonly currentRegisteredDeviceKey: string | null | undefined;
}

interface DesktopRuntimeIndexes {
  readonly deviceById: ReadonlyMap<number, CmmdDeviceRegistryDevice>;
  readonly hostById: ReadonlyMap<number, CmmdDeviceRegistryHost>;
}

interface CurrentDesktopIdentity {
  readonly hostIdentity: string | null;
  readonly deviceId: number | null;
}

interface DesktopRuntimeCandidate {
  readonly hostIdentity: string;
  readonly group: RegisteredDesktopRuntimeGroup;
}

function physicalHostIdentity(input: {
  readonly runtime?: CmmdDeviceRegistryRuntime | null;
  readonly device?: CmmdDeviceRegistryDevice | null;
}): string | null {
  const hostId = input.runtime?.hostId ?? input.device?.hostId ?? null;
  if (hostId !== null) return `host-id:${hostId}`;
  const hostKey = input.device?.hostKey?.trim() ?? "";
  return hostKey.length > 0 ? `host-key:${hostKey}` : null;
}

function buildIndexes(input: RegisteredDesktopRuntimeMapInput): DesktopRuntimeIndexes {
  return {
    deviceById: new Map((input.registeredDevices ?? []).map((device) => [device.id, device])),
    hostById: new Map((input.registeredHosts ?? []).map((host) => [host.id, host])),
  };
}

function findCurrentRuntime(
  input: RegisteredDesktopRuntimeMapInput,
): CmmdDeviceRegistryRuntime | null {
  if (input.currentRegisteredRuntimeId === null || input.currentRegisteredRuntimeId === undefined) {
    return null;
  }
  return (
    input.registeredRuntimes?.find((runtime) => runtime.id === input.currentRegisteredRuntimeId) ??
    null
  );
}

function findCurrentDeviceByKey(
  input: RegisteredDesktopRuntimeMapInput,
): CmmdDeviceRegistryDevice | null {
  if (input.currentRegisteredDeviceKey === null || input.currentRegisteredDeviceKey === undefined) {
    return null;
  }
  return (
    input.registeredDevices?.find(
      (device) => device.deviceKey === input.currentRegisteredDeviceKey,
    ) ?? null
  );
}

function findCurrentDeviceByRuntime(
  runtime: CmmdDeviceRegistryRuntime | null,
  indexes: DesktopRuntimeIndexes,
): CmmdDeviceRegistryDevice | null {
  if (runtime?.deviceId === null || runtime?.deviceId === undefined) return null;
  return indexes.deviceById.get(runtime.deviceId) ?? null;
}

function resolveCurrentDesktopIdentity(
  input: RegisteredDesktopRuntimeMapInput,
  indexes: DesktopRuntimeIndexes,
): CurrentDesktopIdentity {
  const currentRuntime = findCurrentRuntime(input);
  const currentDevice =
    findCurrentDeviceByKey(input) ?? findCurrentDeviceByRuntime(currentRuntime, indexes);

  return {
    hostIdentity: physicalHostIdentity({ runtime: currentRuntime, device: currentDevice }),
    deviceId: currentDevice?.id ?? currentRuntime?.deviceId ?? null,
  };
}

function isEligibleRegisteredDesktopRuntime(
  runtime: CmmdDeviceRegistryRuntime,
  currentRegisteredRuntimeId: string | null | undefined,
): boolean {
  return (
    runtime.runtimeKind === "desktop-local" &&
    runtime.status !== "revoked" &&
    runtime.source === "registered" &&
    runtime.id !== currentRegisteredRuntimeId
  );
}

function isAuthorizedDevice(
  device: CmmdDeviceRegistryDevice | undefined,
): device is CmmdDeviceRegistryDevice {
  return Boolean(
    device && device.trustState !== "revoked" && device.authorization?.revoked !== true,
  );
}

function resolveEligibleDevice(input: {
  readonly runtime: CmmdDeviceRegistryRuntime;
  readonly indexes: DesktopRuntimeIndexes;
  readonly currentDesktop: CurrentDesktopIdentity;
  readonly currentRegisteredRuntimeId: string | null | undefined;
}): CmmdDeviceRegistryDevice | null {
  if (!isEligibleRegisteredDesktopRuntime(input.runtime, input.currentRegisteredRuntimeId)) {
    return null;
  }
  if (input.runtime.deviceId === null) return null;
  const device = input.indexes.deviceById.get(input.runtime.deviceId);
  if (!isAuthorizedDevice(device)) return null;
  if (
    physicalHostIdentity({ runtime: input.runtime, device }) === null &&
    input.runtime.status !== "online"
  ) {
    return null;
  }
  if (input.currentDesktop.deviceId === input.runtime.deviceId) return null;
  return device;
}

function resolveRegisteredHost(
  runtime: CmmdDeviceRegistryRuntime,
  device: CmmdDeviceRegistryDevice,
  indexes: DesktopRuntimeIndexes,
): CmmdDeviceRegistryHost | undefined {
  const hostId = runtime.hostId ?? device.hostId ?? null;
  return hostId === null ? undefined : indexes.hostById.get(hostId);
}

function isCurrentPhysicalHost(
  hostIdentity: string | null,
  currentDesktop: CurrentDesktopIdentity,
): boolean {
  return hostIdentity !== null && hostIdentity === currentDesktop.hostIdentity;
}

function buildRuntimeCandidate(input: {
  readonly runtime: CmmdDeviceRegistryRuntime;
  readonly indexes: DesktopRuntimeIndexes;
  readonly currentDesktop: CurrentDesktopIdentity;
  readonly currentRegisteredRuntimeId: string | null | undefined;
}): DesktopRuntimeCandidate | null {
  const device = resolveEligibleDevice(input);
  if (device === null) return null;

  const hostIdentity = physicalHostIdentity({ runtime: input.runtime, device });
  if (isCurrentPhysicalHost(hostIdentity, input.currentDesktop)) return null;

  const host = resolveRegisteredHost(input.runtime, device, input.indexes);
  return {
    hostIdentity: hostIdentity ?? `device-id:${input.runtime.deviceId}`,
    group: {
      runtime: input.runtime,
      device,
      label: host?.displayName ?? device.displayName,
      platformOs: host?.platform ?? device.platform ?? null,
    },
  };
}

function runtimeTimestamp(runtime: CmmdDeviceRegistryRuntime): number {
  return runtime.lastSeenAt ? Date.parse(runtime.lastSeenAt) || 0 : 0;
}

function keepNewestRuntimeForHost(
  runtimeByPhysicalHost: Map<string, RegisteredDesktopRuntimeGroup>,
  candidate: DesktopRuntimeCandidate,
): void {
  const existing = runtimeByPhysicalHost.get(candidate.hostIdentity);
  if (
    existing === undefined ||
    runtimeTimestamp(candidate.group.runtime) > runtimeTimestamp(existing.runtime)
  ) {
    runtimeByPhysicalHost.set(candidate.hostIdentity, candidate.group);
  }
}

export function buildRegisteredDesktopRuntimeMap(
  input: RegisteredDesktopRuntimeMapInput,
): ReadonlyMap<EnvironmentId, RegisteredDesktopRuntimeGroup> {
  const indexes = buildIndexes(input);
  const currentDesktop = resolveCurrentDesktopIdentity(input, indexes);
  const runtimeByPhysicalHost = new Map<string, RegisteredDesktopRuntimeGroup>();

  for (const runtime of input.registeredRuntimes ?? []) {
    const candidate = buildRuntimeCandidate({
      runtime,
      indexes,
      currentDesktop,
      currentRegisteredRuntimeId: input.currentRegisteredRuntimeId,
    });
    if (candidate !== null) keepNewestRuntimeForHost(runtimeByPhysicalHost, candidate);
  }

  return new Map(
    [...runtimeByPhysicalHost.values()].map((group) => [group.runtime.id as EnvironmentId, group]),
  );
}
